import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import cx from "classnames";

import type { ProfileFoodNoise } from "types/profile";
import type { ResultsFoodNoiseType } from "types/results";

import { AsHtml } from "utils/translate";
import SiteWrapper from "components/SiteWrapper";
import ContentGutter from "components/ContentGutter";
import { foodNoiseCharts } from "./charts";
import { pageView, setPageData } from "utils/analytics";
import { SESSION_STORAGE_FOOD_NOISE } from "components/Profile/constants";
import { foodNoiseContent } from "./content";
import routes from "routes";
import useUrlSlugContext from "components/UrlSlugContext";

import styles from "./styles.module.scss";

export interface ResultsFoodNoiseProps {
  results?: ResultsFoodNoiseType;
  resultsRestored?: boolean;
  profile?: ProfileFoodNoise;
  page?: number;
}

const ResultsFoodNoise: React.FC<ResultsFoodNoiseProps> = ({
  results: resultsInit,
  resultsRestored = false,
  profile: profileInit,
  page: pageInit = 1
}) => {
  const history = useHistory();
  const location = useLocation();

  const {
    results: resultsState,
    profile: profileState,
    page: pageState
  } = (location.state || {}) as Pick<
    ResultsFoodNoiseProps,
    "results" | "profile" | "page"
  >;
  const results = resultsState || resultsInit;
  const profile = profileState || profileInit;

  const {
    sum = 0,
    score = 0,
    emailStatus: resultsEmailStatus,
    isActive: resultsIsActive
  } = results || {};
  const gender = profile?.gender === "M" ? "men" : "women";
  const chart = foodNoiseCharts[profile?.gender || "F"][sum || 0];
  const content = foodNoiseContent({ sum, score, gender, chart });
  const page = pageState || pageInit;

  const sessionStorageFoodNoise = window.sessionStorage?.getItem(
    SESSION_STORAGE_FOOD_NOISE
  );
  const { uuid } = JSON.parse(sessionStorageFoodNoise || "{}");

  const isActive =
    typeof resultsIsActive === "boolean" ? resultsIsActive : false;
  const emailStatus =
    {
      Active: "active",
      New: "new",
      EmailInOtherMkt: "registered",
      Registered: "registered"
    }[resultsEmailStatus || ""] || "new";

  const higherLower = isActive
    ? score >= 50
      ? "higherActive"
      : "lowerActive"
    : score >= 50
      ? "higher"
      : "lower";

  useEffect(() => {
    if (resultsRestored) {
      return;
    }

    if (page === 1) {
      pageView("result", "fnqz");
    } else {
      pageView("result-2", "fnqz");
    }

    if (!Number.isInteger(sum) || !score || !uuid) {
      return;
    }

    setPageData({
      uuid,
      questionnaire_result: sum,
      user_type: emailStatus
    });
  }, [sum, emailStatus, score, uuid, resultsRestored]);

  // Hides the link back to certain pages
  // if the user originally came from those pages.
  const { queryString } = useUrlSlugContext();
  const fromPage = new URLSearchParams(queryString || "").get("fromPage");

  return (
    <ContentGutter>
      <SiteWrapper size="small">
        {page === 1 && (
          <div className={styles.page}>
            <div className={styles.top}>
              <AsHtml
                className={styles.heading}
                value={content[higherLower].header}
                interpolations={content[higherLower].headerInterpolations}
              />
              <img className={styles.img} src={chart} alt="" />
              <div
                className={cx(styles.content, {
                  [styles.hideItem1]: fromPage === "what-is-food-noise",
                })}
              >
                <AsHtml
                  value={`${content[higherLower].text} `}
                  tagType={content[higherLower].cta ? "span" : "div"}
                />
                {content[higherLower].cta && (
                  <button
                    onClick={() =>
                      history.push(
                        resultsRestored
                          ? routes.resultsRestored2
                          : routes.results2,
                        {
                          results,
                          profile,
                          page: 2
                        }
                      )
                    }
                    className={styles.cta}
                  >
                    {content[higherLower].cta}
                  </button>
                )}
              </div>
            </div>
            <div className={styles.disclaimer}>
              {content[higherLower].disclaimer}
            </div>
          </div>
        )}
        {page === 2 &&
          content[higherLower].header2 &&
          content[higherLower].items && (
            <div className={styles.page}>
              <h1 className={styles.heading2}>
                {content[higherLower].header2}
              </h1>
              <div className={styles.items}>
                {content[higherLower].items.map((item, index) => (
                  <div key={index} className={styles.item}>
                    <div>
                      <img
                        className={styles.itemImage}
                        src={item.image}
                        alt=""
                      />
                    </div>
                    <div>
                      <div className={styles.itemText}>
                        {`${item.text} `}
                        <a
                          href={item.link}
                          className={styles.itemCta}
                        >
                          {item.cta}
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
      </SiteWrapper>
    </ContentGutter>
  );
};

export default ResultsFoodNoise;
